import { hc } from 'hono/client';

import type { AppType } from '../../worker/app';
import { csrfHeaders } from './auth-context';
import { track } from './telemetry';

/**
 * Hono RPC client — typed against the Worker's AppType.
 *
 * Wave 2 slices call e.g. `api.api.exchanges[':id'].confirm.$post({...})`
 * and get end-to-end types from the Worker route definitions.
 *
 * Same-origin: relative base URL works because the SPA + API are the same Worker.
 */
export const api = hc<AppType>('/');

export type Api = typeof api;

/**
 * ApiError — thrown by `fetchOk` (and any caller using the same handler shape)
 * when a non-2xx response comes back from the Worker. Carries the HTTP status
 * plus a human-readable message that's safe to render directly to the user.
 *
 * The `message` is resolved against three shapes, in order:
 *   1. JSON `{ error: { message: string } }` (Hono HTTPException default
 *      under our error handler)
 *   2. JSON `{ message: string }` (some legacy slice routes)
 *   3. text/plain body (Hono HTTPException's default Content-Type when the
 *      handler doesn't override — this is what /api/auth/sign-in returns on
 *      401, which previously crashed the client with "Unexpected token 'W'…
 *      is not valid JSON" — see WW-FUNC-008.)
 *
 * Falls back to a generic "Request failed (status)" if all three are empty.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * fetchOk — fetch wrapper that throws `ApiError` on non-2xx responses,
 * regardless of whether the server returned JSON or text/plain. Use this
 * everywhere the SPA hits a JSON endpoint and wants to render the server
 * message inline.
 *
 * Also caps any text/plain error body at 500 chars to avoid pasting a giant
 * stack trace or Zod issue dump straight into a Mantine Alert (see
 * WW-FUNC-403 for the raw-Zod-leak case — same fix surface).
 */
export async function fetchOk(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (!res.ok) {
    const ct = res.headers.get('content-type') ?? '';
    let message: string;
    if (ct.includes('application/json')) {
      const body = (await res
        .json()
        .catch(() => null)) as { error?: { message?: string }; message?: string } | null;
      message = body?.error?.message ?? body?.message ?? `Request failed (${res.status})`;
    } else {
      const text = await res.text().catch(() => '');
      message = text.slice(0, 500) || `Request failed (${res.status})`;
    }
    // Telemetry: every API failure goes through here. Path-only, no querystring,
    // capped message length — no PII surface. Skip emits to the telemetry
    // endpoint itself to avoid feedback loops.
    const endpoint = resolveEndpointPath(input);
    if (!endpoint.startsWith('/api/telemetry/')) {
      track('error.api', {
        endpoint,
        status: res.status,
        message: message.slice(0, 200),
      });
    }
    throw new ApiError(res.status, message);
  }
  return res;
}

/**
 * fetchJson — `fetchOk` + `res.json()` in one call, with the result narrowed
 * to `T`. Use for any GET that returns JSON; relieves callers from the
 * `as unknown as T` cast + the `res.ok` re-check.
 *
 * Sets `credentials: 'include'` so the session cookie rides along even if a
 * caller forgets — same-origin requests already include it, but explicit is
 * safer.
 */
export async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetchOk(path, {
    credentials: 'include',
    ...init,
    headers: { ...init?.headers },
  });
  return (await res.json()) as T;
}

/**
 * mutateJson — POST/PATCH/DELETE wrapper that auto-spreads `csrfHeaders()`,
 * serialises `body` to JSON, and parses the response as `T`. The CSRF gate
 * lives in the global `/api/*` middleware; using this helper keeps the
 * X-CSRF-Token spread from being a per-call ritual that's easy to forget.
 *
 * Pass `body: undefined` for an empty-payload POST (the default content-type
 * header is still set so the worker zod validators don't reject).
 */
export async function mutateJson<T>(
  path: string,
  body?: unknown,
  init?: RequestInit & { method?: 'POST' | 'PATCH' | 'PUT' | 'DELETE' },
): Promise<T> {
  const method = init?.method ?? 'POST';
  const reqInit: RequestInit = {
    method,
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeaders(),
      ...init?.headers,
    },
  };
  if (body !== undefined) reqInit.body = JSON.stringify(body);
  const res = await fetchOk(path, reqInit);
  // Some mutation endpoints respond 204 No Content — caller's T should be void.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function resolveEndpointPath(input: RequestInfo | URL): string {
  try {
    if (typeof input === 'string') {
      return input.split('?')[0] ?? input;
    }
    if (input instanceof URL) {
      return input.pathname;
    }
    return input.url.split('?')[0] ?? input.url;
  } catch {
    return 'unknown';
  }
}
